import { css } from 'styled-components';
import { breakpointsMedia } from '@nubank/nuds-web/styles/breakpoints';
import { isType } from '@nubank/nuds-web/utils/isType/isType';

import { requireImage } from '../../../utils/requireImage';
import isCMSImage from '../../../utils/cms/images/isCMSImage';
import { addCMSQueryParametersToPath } from '../../../utils/cms/images/addCMSQueryParameters';
import { isExternalImageSource } from '../utils';

import addTypeBackgroundImage, { handleNotSupportedType } from './addTypeBackgroundImage';

const WEBP_DEFAULT_VALUE = true;

const handleBackground = (path, webp = WEBP_DEFAULT_VALUE, breakpoint, dpr) => {
  if (path === 'none') {
    return `
      background-image: none;
      ${handleNotSupportedType('background-image: none;')}
    `;
  }

  if (isCMSImage(path)) {
    const paths = addCMSQueryParametersToPath(path, breakpoint, dpr);
    return (webp
      ? addTypeBackgroundImage(paths)
      : `background-image: url(${requireImage(paths)});`
    );
  }

  if (isExternalImageSource(path)) {
    return `background-image: url(${path});`;
  }

  return (webp
    ? addTypeBackgroundImage(path)
    : `background-image: url(${requireImage(path)});`
  );
};

function createDPR(dpr) {
  return {
    pixelRatio: dpr,
    dpi: 96 * dpr,
    ddpx: dpr,
  };
}
const DPRValuesByDensity = {
  '1x': createDPR(1),
  '2x': createDPR(2),
  '3x': createDPR(2.5),
};

function handlePixelRatioBreakpoints(srcSetDPR, webp = WEBP_DEFAULT_VALUE, breakpoint) {
  const has1xImage = srcSetDPR['1x'];
  if (!has1xImage) {
    throw new Error('You must provide the "1x" image to be used as default');
  }

  const breakpointDPRs = Object.entries(srcSetDPR);
  return css`
    ${breakpointDPRs.map(([currentDPR, currentImagePath]) => {
    const currentDensityDPRValues = DPRValuesByDensity[currentDPR];

    if (currentDPR === '1x') {
      return css`
          ${handleBackground(currentImagePath, webp, breakpoint, currentDPR)}
        `;
    }

    return css`
      @media
      only screen and (min-device-pixel-ratio: ${currentDensityDPRValues.pixelRatio}),
      only screen and (        min-device-pixel-ratio: ${currentDensityDPRValues.pixelRatio}),
      only screen and (                min-resolution: ${currentDensityDPRValues.dpi}dpi),
      only screen and (                min-resolution: ${currentDensityDPRValues.ddpx}dppx) {
        ${handleBackground(currentImagePath, webp, breakpoint, currentDPR)}
      }
    `;
  })}
  `;
}

function handleBackgroundWithDPR(srcSetItem, webp = WEBP_DEFAULT_VALUE, breakpoint) {
  const isPixelRatioBreakpointPath = !isType('string', srcSetItem);
  return isPixelRatioBreakpointPath
    ? handlePixelRatioBreakpoints(srcSetItem, webp, breakpoint)
    : handleBackground(srcSetItem, webp, breakpoint);
}

export default function backgroundImage(srcSet, webp = WEBP_DEFAULT_VALUE) {
  if (isType('string', srcSet)) {
    return handleBackground(srcSet, webp);
  }

  const breakpointsWithBackgroundWithDPR = Object.entries(srcSet)
    .reduce((acc, [breakpoint, path]) => (
      {
        ...acc,
        [breakpoint]: css`
            ${handleBackgroundWithDPR(path, webp, breakpoint)}
        `,
      }), {});

  return breakpointsMedia(breakpointsWithBackgroundWithDPR);
}
